/**
 * Client assignment QuestionnaireResponse model (ENG-3764 D1, D2).
 *
 * Assigning a chart document, form or assessment to a client means saving the QR the client will fill out:
 * `status: in-progress`, chart status `pending-client`, the template-category tag (`forms` or `measures`), the
 * episode extension, and the assigner's choices in `CLIENT_ASSIGNMENT_REQUEST_EXT` (due date, message). The
 * `task-events` client-assignment listener creates the client Task from that save; the Task carries the QR
 * in an `assigned-response` input (forms, assessments) or as its `focus` (chart documents).
 *
 * Browser-safe: pure helpers, no Medplum client.
 */
import type { Bundle, CodeableConcept, Extension, Provenance, QuestionnaireResponse, Reference, Task } from '@medplum/fhirtypes';

import {
  ASSIGNED_RESPONSE_INPUT_CODE,
  CHART_DOCUMENT_STATUS_CANCELLED,
  CHART_DOCUMENT_STATUS_EXT,
  CHART_DOCUMENT_STATUS_PENDING_CLIENT,
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  CLIENT_ASSESSMENT_OCCURRENCE_TASK_IDENTIFIER_SYSTEM,
  CLIENT_ASSIGNMENT_CANCEL_PROVENANCE_TAG_CODE,
  CLIENT_ASSIGNMENT_PROVENANCE_TAG_SYSTEM,
  CLIENT_ASSIGNMENT_REQUEST_EXT,
  CLIENT_DOCUMENT_COMPLETION_CODE,
  CLIENT_DOCUMENT_COMPLETION_TASK_IDENTIFIER_SYSTEM,
  CLIENT_FORM_ASSIGNMENT_CODE,
  CLIENT_FORM_ASSIGNMENT_TASK_IDENTIFIER_SYSTEM,
  CLIENT_SIGNATURE_REQUEST_CODE,
  CLIENT_SIGNATURE_REQUEST_TASK_IDENTIFIER_SYSTEM,
  PROVENANCE_ACTIVITY_CANCEL_CODE,
  PROVENANCE_ACTIVITY_DATA_OPERATION_SYSTEM,
  TASK_INPUT_SYSTEM,
  TASK_TYPE_SYSTEM,
  TEMPLATE_CATEGORY_SYSTEM,
} from '../constants';

/** Template categories a client assignment QR carries; the QR Subscription and the portal read depend on them. */
export type AssignmentTemplateCategory = 'forms' | 'measures';

export interface ClientAssignmentRequest {
  /** Task due date (`restriction.period.end`). */
  due?: string;
  /** The assigner's note to the client (`Task.note`, used as the notification body). */
  message?: string;
}

const SUB_EXTENSION = { due: 'due', message: 'message' } as const;

export function getChartDocumentStatusCode(qr: QuestionnaireResponse): string | undefined {
  return qr.extension?.find((e) => e.url === CHART_DOCUMENT_STATUS_EXT)?.valueCode;
}

/** The QR is an unanswered client assignment: `in-progress` with chart status `pending-client`. */
export function isPendingClientAssignment(qr: QuestionnaireResponse): boolean {
  return qr.status === 'in-progress' && getChartDocumentStatusCode(qr) === CHART_DOCUMENT_STATUS_PENDING_CLIENT;
}

/** `forms` or `measures` when the QR carries that template-category tag (forms wins when both are present). */
export function getAssignmentTemplateCategory(qr: QuestionnaireResponse): AssignmentTemplateCategory | undefined {
  const codes = new Set((qr.meta?.tag ?? []).filter((t) => t.system === TEMPLATE_CATEGORY_SYSTEM).map((t) => t.code));
  if (codes.has('forms')) return 'forms';
  if (codes.has('measures')) return 'measures';
  return undefined;
}

export function getClientAssignmentRequest(qr: QuestionnaireResponse): ClientAssignmentRequest {
  const ext = qr.extension?.find((e) => e.url === CLIENT_ASSIGNMENT_REQUEST_EXT);
  const sub = (url: string): Extension | undefined => ext?.extension?.find((e) => e.url === url);
  const due = sub(SUB_EXTENSION.due)?.valueDateTime;
  const message = sub(SUB_EXTENSION.message)?.valueString?.trim();
  return {
    ...(due ? { due } : {}),
    ...(message ? { message } : {}),
  };
}

/** Replace the assignment request extension; an empty request removes it. */
export function setClientAssignmentRequest(qr: QuestionnaireResponse, request: ClientAssignmentRequest): QuestionnaireResponse {
  const rest = (qr.extension ?? []).filter((e) => e.url !== CLIENT_ASSIGNMENT_REQUEST_EXT);
  const sub: Extension[] = [
    ...(request.due ? [{ url: SUB_EXTENSION.due, valueDateTime: request.due }] : []),
    ...(request.message?.trim() ? [{ url: SUB_EXTENSION.message, valueString: request.message.trim() }] : []),
  ];
  const extension = sub.length > 0 ? [...rest, { url: CLIENT_ASSIGNMENT_REQUEST_EXT, extension: sub }] : rest;
  return { ...qr, extension: extension.length > 0 ? extension : undefined };
}

/** The `assigned-response` input a form or assessment Task carries (ENG-3764 D1). */
export function assignedResponseInput(qrId: string): NonNullable<Task['input']>[number] {
  return {
    type: { coding: [{ system: TASK_INPUT_SYSTEM, code: ASSIGNED_RESPONSE_INPUT_CODE, display: 'Assigned response' }] },
    valueReference: { reference: `QuestionnaireResponse/${qrId}` },
  };
}

/** The QR a client Task assigns: its `assigned-response` input, else a QuestionnaireResponse focus (chart documents). */
export function getAssignedResponseReference(task: Task): Reference | undefined {
  const input = task.input?.find((entry) =>
    entry.type?.coding?.some((c) => c.system === TASK_INPUT_SYSTEM && c.code === ASSIGNED_RESPONSE_INPUT_CODE));
  if (input?.valueReference?.reference?.startsWith('QuestionnaireResponse/')) return input.valueReference;
  if (task.focus?.reference?.startsWith('QuestionnaireResponse/')) return task.focus;
  return undefined;
}

/** The template a Questionnaire canonical names, without its version: `url|version` → `url`. */
export function canonicalTemplateUrl(canonical: string | undefined): string | undefined {
  if (!canonical) return undefined;
  return canonical.split('|')[0];
}

/** Task types the listener creates from an assignment QR (ENG-3764 D2). */
export const CLIENT_ASSIGNMENT_TASK_CODES: ReadonlySet<string> = new Set([
  CLIENT_SIGNATURE_REQUEST_CODE,
  CLIENT_DOCUMENT_COMPLETION_CODE,
  CLIENT_FORM_ASSIGNMENT_CODE,
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
]);

/** Identifier systems that key a client Task on its assignment QR id (D3): chart documents, forms and assessments. */
export const CLIENT_ASSIGNMENT_TASK_IDENTIFIER_SYSTEMS: readonly string[] = [
  CLIENT_SIGNATURE_REQUEST_TASK_IDENTIFIER_SYSTEM,
  CLIENT_DOCUMENT_COMPLETION_TASK_IDENTIFIER_SYSTEM,
  CLIENT_FORM_ASSIGNMENT_TASK_IDENTIFIER_SYSTEM,
  CLIENT_ASSESSMENT_OCCURRENCE_TASK_IDENTIFIER_SYSTEM,
];

/**
 * A client Task backed by an assignment QR: a client assignment type that names its QR (`assigned-response` input,
 * or a QR focus for chart documents). Staff cancel it by stopping that QR (D27); a legacy Task created before
 * ENG-3764 names no QR and cannot be cancelled from a staff screen (D29: no user edits a Task).
 */
export function isClientAssignmentTask(task: Task): boolean {
  const typed = task.code?.coding?.some((c) => c.system === TASK_TYPE_SYSTEM && CLIENT_ASSIGNMENT_TASK_CODES.has(c.code ?? '')) === true;
  return typed && getAssignedResponseReference(task) !== undefined;
}

/**
 * The staff cancel of a client assignment (D27): `stopped` and chart status `cancelled`. Why and by whom is not on
 * the QR: it is the Provenance `clientAssignmentCancelTransaction` writes with the stop. Tags are kept: the stopped-QR
 * Subscription matches on the forms / measures template tag, and the D25 `writeConstraint` requires it on the new
 * version.
 */
export function cancelClientAssignmentResponse(qr: QuestionnaireResponse): QuestionnaireResponse {
  const rest = (qr.extension ?? []).filter((e) => e.url !== CHART_DOCUMENT_STATUS_EXT);
  return {
    ...qr,
    status: 'stopped',
    extension: [...rest, { url: CHART_DOCUMENT_STATUS_EXT, valueCode: CHART_DOCUMENT_STATUS_CANCELLED }],
  };
}

const CLIENT_ASSIGNMENT_CANCEL_PROVENANCE_TAG = {
  system: CLIENT_ASSIGNMENT_PROVENANCE_TAG_SYSTEM,
  code: CLIENT_ASSIGNMENT_CANCEL_PROVENANCE_TAG_CODE,
} as const;

/**
 * The Provenance of a staff cancel (D27): `target` the QR (by id: the stop's version does not exist until the
 * transaction commits, so `recorded` and `occurredDateTime` date it instead), `activity` v3 DataOperation `CANCEL`,
 * `reason` the staff reason when one was given, `agent.who` the staff member, and the cancel tag that the staff
 * policies' create entry matches.
 */
export function clientAssignmentCancelProvenance(
  qrId: string,
  agent: Reference,
  reason: CodeableConcept | undefined,
  now: string,
): Provenance {
  return {
    resourceType: 'Provenance',
    meta: { tag: [{ ...CLIENT_ASSIGNMENT_CANCEL_PROVENANCE_TAG }] },
    target: [{ reference: `QuestionnaireResponse/${qrId}` }],
    occurredDateTime: now,
    recorded: now,
    activity: {
      coding: [{ system: PROVENANCE_ACTIVITY_DATA_OPERATION_SYSTEM, code: PROVENANCE_ACTIVITY_CANCEL_CODE, display: 'cancel' }],
    },
    ...(reason ? { reason: [reason] } : {}),
    agent: [{ who: { reference: agent.reference as string } }],
  };
}

/**
 * The D27 staff cancel as one transaction: the QR stop (PUT, If-Match on the stored version when known) and its
 * Provenance. Medplum commits both or neither and queues Subscription jobs after the commit, so the stopped-QR
 * listener never sees the stop without its Provenance.
 */
export function clientAssignmentCancelTransaction(
  qr: QuestionnaireResponse,
  agent: Reference,
  reason: CodeableConcept | undefined,
  now: string,
): Bundle {
  if (!qr.id) throw new Error('The assignment QuestionnaireResponse has no id');
  const versionId = qr.meta?.versionId;
  return {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [
      {
        resource: cancelClientAssignmentResponse(qr),
        request: {
          method: 'PUT',
          url: `QuestionnaireResponse/${qr.id}`,
          ...(versionId ? { ifMatch: `W/"${versionId}"` } : {}),
        },
      },
      {
        resource: clientAssignmentCancelProvenance(qr.id, agent, reason, now),
        request: { method: 'POST', url: 'Provenance' },
      },
    ],
  };
}

/** A tagged staff cancel Provenance (`clientAssignmentCancelProvenance`) of this QR. */
export function isClientAssignmentCancelProvenance(provenance: Provenance, qrId: string): boolean {
  return (provenance.meta?.tag ?? []).some((tag) =>
    tag.system === CLIENT_ASSIGNMENT_PROVENANCE_TAG_SYSTEM && tag.code === CLIENT_ASSIGNMENT_CANCEL_PROVENANCE_TAG_CODE)
    && (provenance.activity?.coding ?? []).some((c) =>
      c.system === PROVENANCE_ACTIVITY_DATA_OPERATION_SYSTEM && c.code === PROVENANCE_ACTIVITY_CANCEL_CODE)
    && (provenance.target ?? []).some((ref) => ref.reference === `QuestionnaireResponse/${qrId}`);
}

/**
 * The reason of the stopper's own cancel Provenance for this QR: the most recent (`recorded`) cancel Provenance whose
 * `agent.who` is the stopper and whose server-set `meta.author` is the stopper too, so nobody else can state why
 * someone stopped it. Undefined when there is none, or it gives no reason.
 */
export function clientAssignmentCancelReason(
  provenances: readonly Provenance[],
  qrId: string,
  stopper: string,
): CodeableConcept | undefined {
  const own = provenances
    .filter((provenance) => isClientAssignmentCancelProvenance(provenance, qrId)
      && provenance.meta?.author?.reference === stopper
      && (provenance.agent ?? []).some((agent) => agent.who?.reference === stopper))
    .sort((a, b) => (b.recorded ?? '').localeCompare(a.recorded ?? ''));
  return own[0]?.reason?.[0];
}
